import { useState } from "react";

import {
  ICustomFieldDefinition,
  ICustomFieldType,
  ICustomFieldValidation,
} from "../../../../@types/admin/AdminHospitalDetails";
import {
  useCreateCustomField,
  useUpdateCustomField,
} from "../../../../hooks/mutations/admin/hospitalDetails";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  AdminSelectField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  ReadOnlyValue,
} from "../../shared";

interface ICustomFieldModalProps {
  hospitalGroupId: number;
  /** Absent for a create. */
  field?: ICustomFieldDefinition;
  onDone: () => void;
}

const TYPES: { value: ICustomFieldType; label: string; hint: string }[] = [
  { value: "FreeText", label: "Free text", hint: "Any text, length-capped." },
  { value: "Integer", label: "Whole number", hint: "Range-checked." },
  { value: "Decimal", label: "Decimal number", hint: "Range-checked." },
  { value: "Date", label: "Date", hint: "Stored as yyyy-MM-dd." },
  { value: "YesNo", label: "Yes / no", hint: "A single checkbox." },
  {
    value: "Dropdown",
    label: "Dropdown",
    hint: "One choice from the options below.",
  },
  {
    value: "Multiselect",
    label: "Multi-select",
    hint: "Several choices from the options below.",
  },
];

/** The types whose answers come from a fixed option list. */
const OPTION_TYPES: ICustomFieldType[] = ["Dropdown", "Multiselect"];
/** The types that carry a numeric range. */
const RANGE_TYPES: ICustomFieldType[] = ["Integer", "Decimal"];

/** `a-z0-9_` — the key is used as an identifier by the answer rows. */
const KEY_PATTERN = /^[a-z0-9_]+$/;

/**
 * Creates or edits one of the hospital's custom-field definitions — Retool's
 * `moduleCustomFields` (EN-5175).
 *
 * ⚠️ **`key` is immutable once created.** `UpdateCustomFieldDefinitionRequestDto`
 * has no `key` at all, because the recorded answers are keyed on it — renaming
 * would orphan every answer. The edit form shows it read-only rather than
 * letting an operator try.
 *
 * `isActive` is sent on update because that is how a deactivated field is
 * brought back; there is no separate reactivate route.
 */
export function CustomFieldModal({
  hospitalGroupId,
  field,
  onDone,
}: ICustomFieldModalProps) {
  const isEdit = field !== undefined;
  const { showToast } = useToast();
  const { createCustomField, isCreatingCustomField } = useCreateCustomField();
  const { updateCustomField, isUpdatingCustomField } = useUpdateCustomField();

  const [label, setLabel] = useState(field?.label ?? "");
  const [key, setKey] = useState(field?.key ?? "");
  const [type, setType] = useState<ICustomFieldType>(field?.type ?? "FreeText");
  const [optionsText, setOptionsText] = useState(
    (field?.options ?? []).join("\n")
  );
  const [minValue, setMinValue] = useState(
    field?.validation?.minValue != null ? String(field.validation.minValue) : ""
  );
  const [maxValue, setMaxValue] = useState(
    field?.validation?.maxValue != null ? String(field.validation.maxValue) : ""
  );
  const [maxLength, setMaxLength] = useState(
    field?.validation?.maxLength != null
      ? String(field.validation.maxLength)
      : ""
  );
  const [touched, setTouched] = useState(false);

  const isSaving = isCreatingCustomField || isUpdatingCustomField;

  const options = optionsText
    .split("\n")
    .map((option) => option.trim())
    .filter(Boolean);

  const needsOptions = OPTION_TYPES.includes(type);
  const hasRange = RANGE_TYPES.includes(type);

  const keyError = (() => {
    if (isEdit) return undefined;
    if (!key.trim()) return "Key is required.";
    if (!KEY_PATTERN.test(key.trim())) {
      return "Lower-case letters, numbers and underscores only.";
    }
    return undefined;
  })();

  const errors = {
    label: label.trim() ? undefined : "Label is required.",
    key: keyError,
    options:
      needsOptions && options.length < 2
        ? "A dropdown or multi-select needs at least two options."
        : undefined,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const validation: ICustomFieldValidation = {
    minValue: hasRange && minValue !== "" ? Number(minValue) : null,
    maxValue: hasRange && maxValue !== "" ? Number(maxValue) : null,
    maxLength:
      type === "FreeText" && maxLength !== "" ? Number(maxLength) : null,
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (hasErrors) return;

    try {
      if (isEdit) {
        await updateCustomField({
          id: field!.id,
          hospitalGroupId,
          request: {
            label: label.trim(),
            type,
            options: needsOptions ? options : undefined,
            validation,
            // Preserved: this dialog edits the definition, it does not
            // deactivate. Reactivation is its own action on the list.
            isActive: field!.isActive,
          },
        });
        showToast("The custom field was updated.", "success");
      } else {
        await createCustomField({
          request: {
            hospitalGroupId,
            label: label.trim(),
            key: key.trim(),
            type,
            options: needsOptions ? options : undefined,
            validation,
          },
        });
        showToast("The custom field was created.", "success");
      }
      onDone();
    } catch (error) {
      // The endpoint rejects a duplicate key with a message naming it.
      showToast(
        errorToastMessage(error, "There was an error saving the custom field."),
        "warning"
      );
    }
  };

  const selectedType = TYPES.find((entry) => entry.value === type);

  const submitLabel = (() => {
    if (isSaving) return "Saving...";
    return isEdit ? "Save changes" : "Create field";
  })();

  return (
    <ModalContainer>
      <ModalTitle>
        {isEdit ? "Edit custom field" : "New custom field"}
      </ModalTitle>
      <ModalForm>
        {!isEdit && (
          <ModalBodyText>
            A custom field applies to the whole hospital. Each ward then chooses
            whether to use it, and where it appears.
          </ModalBodyText>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="custom-field-label">Label</FilterLabel>
          <FilterInput
            id="custom-field-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          {touched && errors.label && <FieldError>{errors.label}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="custom-field-key">Key</FilterLabel>
          {isEdit ? (
            <>
              <ReadOnlyValue>{field!.key}</ReadOnlyValue>
              <FieldHint>
                The key can&apos;t change — the answers already recorded are
                keyed on it.
              </FieldHint>
            </>
          ) : (
            <>
              <FilterInput
                id="custom-field-key"
                type="text"
                value={key}
                onChange={(e) => setKey(e.target.value.toLowerCase())}
              />
              {touched && errors.key && <FieldError>{errors.key}</FieldError>}
              <FieldHint>
                Permanent once saved, so choose it carefully.
              </FieldHint>
            </>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="custom-field-type">Type</FilterLabel>
          <AdminSelectField
            id="custom-field-type"
            value={type}
            onChange={(e) => setType(e.target.value as ICustomFieldType)}
          >
            {TYPES.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </AdminSelectField>
          {selectedType && <FieldHint>{selectedType.hint}</FieldHint>}
        </FilterGroup>

        {needsOptions && (
          <FilterGroup>
            <FilterLabel htmlFor="custom-field-options">Options</FilterLabel>
            <textarea
              id="custom-field-options"
              rows={5}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
            />
            {touched && errors.options && (
              <FieldError>{errors.options}</FieldError>
            )}
            <FieldHint>One per line.</FieldHint>
          </FilterGroup>
        )}

        {hasRange && (
          <>
            <FilterGroup>
              <FilterLabel htmlFor="custom-field-min">Minimum</FilterLabel>
              <FilterInput
                id="custom-field-min"
                type="number"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
              />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel htmlFor="custom-field-max">Maximum</FilterLabel>
              <FilterInput
                id="custom-field-max"
                type="number"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
              />
            </FilterGroup>
          </>
        )}

        {type === "FreeText" && (
          <FilterGroup>
            <FilterLabel htmlFor="custom-field-max-length">
              Maximum length
            </FilterLabel>
            <FilterInput
              id="custom-field-max-length"
              type="number"
              value={maxLength}
              onChange={(e) => setMaxLength(e.target.value)}
            />
          </FilterGroup>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving}
            onClick={handleSubmit}
          >
            {submitLabel}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
